/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- This is a legacy component. */
import classNames from 'classnames';
import type { ReactElement, VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { Role } from '../../../../../shared/model-typings';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { useOnMount } from '../../../../hooks';
import OrganizationAccessRequestsModule from '../../../../redux/organization_access_requests';
import type { RolesStore } from '../../../../redux/roles';
import RolesModules from '../../../../redux/roles';
import TeamsModule from '../../../../redux/teams';
import type { ReduxStore } from '../../../../redux/store';
import type { UsersStore } from '../../../../redux/users';
import UsersModule from '../../../../redux/users';
import OrganizationsModule from '../../../../redux/organizations';
import type { AsyncStatus, Organization, OrganizationAccessRequest } from '../../../../types';
import SearchInput from '../../../generic/SearchInput';
import ModalInviteUsers from '../../../modals/ModalInviteUsers';
import Header from '../../../shared/settings/SettingsPageHeader';
import OrganizationAccessRequestsTable from './OrganizationAccessRequestsTable';
import UserInvitationsTable from './UserInvitationsTable';
import UsersTable from './UsersTable';
import { NONE_ROLE } from './constants';
import Loader from '../../../generic/Loader';
import { useRouter } from '../../../../contexts/RouterContext';
import { useUserInvitations, useCreateUserInvitations } from '../../../../hooks/api';

type Tab = 'members' | 'requests' | 'invites';

type OwnProps = {
  organization: Organization;
};

type ReduxStateProps = {
  users: UsersStore['users'];
  organizationAccessRequests: Record<string, OrganizationAccessRequest>;
  roles: RolesStore['roles'];
};

type DispatchProps = {
  inviteExistingAccessRequest: (organizationId: number, accessRequestId: number) => void;
  removeAccessRequest: (organizationId: number, accessRequestId: number) => void;
};

type OrganizationUsersIndexProps = OwnProps & ReduxStateProps & DispatchProps;

const OrganizationUsersIndex = ({
  users,
  roles,
  inviteExistingAccessRequest,
  organization,
  organizationAccessRequests,
  removeAccessRequest,
}: OrganizationUsersIndexProps) => {
  const router = useRouter();
  const [searchValue, setSearchValue] = useState('');
  const [shouldShowInviteModal, setShouldShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [orgRoleFilter, setOrgRoleFilter] = useState<Role | 'None' | null>(null);
  const { data: userInvitations, isLoading, isError, error } = useUserInvitations();
  //  Note: this should probably live in ModalInviteUsers, but that is a class component.
  const {
    mutateAsync: createUserInvitations,
    error: createInviteError,
    isLoading: isCreateInviteIsLoading,
  } = useCreateUserInvitations(organization.id);

  const onSetActiveTabFn = (tab: Tab) => () => {
    setActiveTab(tab);
    setSearchValue('');
  };

  const canInviteUser = window.hasPermission({ action: Action.Invite, resourceType: ResourceType.User });

  //  Handle changing the role filter dropdown.
  const onRoleFilterDropdownSelect = (roleId: number | 'None' | 'clear') => {
    //  'None' is for users who have no org role, `null` is for when users clear the Dropdown.
    if (roleId === NONE_ROLE || roleId === 'clear') {
      return setOrgRoleFilter(roleId === 'clear' ? null : roleId);
    }
    const selectedRole = Object.values(roles).find(role => role.id === roleId);
    return setOrgRoleFilter(selectedRole || null);
  };

  let body: ReactElement;
  switch (activeTab) {
    case 'requests':
      body = (
        <OrganizationAccessRequestsTable
          accessRequests={organizationAccessRequests}
          searchQuery={searchValue}
          onAcceptRequest={requestId => inviteExistingAccessRequest(organization.id, requestId)}
          onIgnoreRequest={requestId => removeAccessRequest(organization.id, requestId)}
      />
      );
      break;
    case 'invites':
      body = (
        <UserInvitationsTable
          invitations={userInvitations ?? []}
          users={users}
          searchQuery={searchValue}
      />
      );
      break;
    case 'members':
    default:
      body = (
        <UsersTable
          users={users}
          searchQuery={searchValue}
          orgRoleFilter={orgRoleFilter}
          router={router}
      />
      );
  }

  return (
    <div className="users-index">
      <Header heading="Users" isPadded>
        <Header.TooltipButton
          id="invite-users-button"
          onClick={() => setShouldShowInviteModal(true)}
          isDisabled={!canInviteUser}
          showTooltip={!canInviteUser}
          tooltip="You are not permitted to invite users."
        >
          Invite Users
        </Header.TooltipButton>
        {isError && (
          <Header.Errors errors={[error.message]} />
        )}
      </Header>
      <ul className="team-settings__sub-tabs">
        <li onClick={onSetActiveTabFn('members')} className={classNames('clickable', { active: activeTab === 'members' })}>
          <span>Members</span> <small>{Object.values(users).length}</small>
        </li>
        {window.on_prem && (
          <li onClick={onSetActiveTabFn('requests')} className={classNames('clickable', { active: activeTab === 'requests' })}>
            <span>Requests</span> <small>{Object.values(organizationAccessRequests).length}</small>
          </li>
        )}
        {canInviteUser && !organization.disable_invite && (
          <li onClick={onSetActiveTabFn('invites')} className={classNames('clickable', { active: activeTab === 'invites' })}>
            <span>Invites</span>
            {' '}
            <small>
              {isLoading ? '...' : userInvitations?.length ?? 0}
            </small>
          </li>
        )}
      </ul>
      <div className="team-settings__search-panel">
        <SearchInput value={searchValue} onChange={ev => setSearchValue(ev.target.value)} />
        {activeTab === 'members' && (
          <span className="margin-left-sm">
            <Dropdown
              id="user-roles-filter"
              onSelect={onRoleFilterDropdownSelect}
            >
              <Dropdown.Toggle>
                {orgRoleFilter === NONE_ROLE ? NONE_ROLE : orgRoleFilter?.name || 'Organization Role'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <MenuItem eventKey="clear">
                  Clear filter
                </MenuItem>
                <MenuItem divider />
                {Object.values(roles)
                  .filter(role => role.scope === 'organization')
                  .map(role => role && (
                    <MenuItem eventKey={role.id} active={role.id === orgRoleFilter?.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                <MenuItem eventKey={NONE_ROLE}>{NONE_ROLE}</MenuItem>
              </Dropdown.Menu>
            </Dropdown>
          </span>
        )}
      </div>
      {body}
      <ModalInviteUsers
        show={shouldShowInviteModal}
        onCreateUserInvitations={createUserInvitations as any as (emails: Set<string>) => Promise<void>}
        createInviteError={createInviteError?.data?.message}
        createInviteIsLoading={isCreateInviteIsLoading}
        onHide={() => {
          setShouldShowInviteModal(false);
        }}
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  users: UsersModule.selectors.getUsersByOrganization,
  organizationAccessRequests: OrganizationAccessRequestsModule.selectors.getActiveRequestsForOrganization,
  roles: RolesModules.selectors.getRoles,
});

const mapDispatchToProps: DispatchProps = {
  inviteExistingAccessRequest: OrganizationAccessRequestsModule.actions.inviteExisting,
  removeAccessRequest: OrganizationAccessRequestsModule.actions.removeAccessRequest,
};

const ConnectedOrganizationUsersIndex = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrganizationUsersIndex);

/**
 * Container component is required for ensuring that organization is fetched before
 * the OrganizationUsersIndex is rendered, because `useUserInvitations` (and other places)
 * in the component rely on Organization being present.
 */
const OrganizationUsersIndexContainer: VoidFunctionComponent<ReduxStateProps & DispatchProps> = (props) => {
  const dispatch = useDispatch();
  const organization = useSelector(OrganizationsModule.selectors.getCurrentOrganization);
  const organizationStatus = useSelector(OrganizationsModule.selectors.getStatus) as AsyncStatus;
  const usersStatus = useSelector(UsersModule.selectors.getStatus) as AsyncStatus;

  useOnMount(async () => {
    dispatch(OrganizationsModule.actions.fetchOne(window.user?.organizationId as number));
    dispatch(UsersModule.actions.fetchUsersByOrganization(window.user?.organizationId as number));
    //  Needed to hydrate the Role column/filter
    dispatch(RolesModules.actions.fetchAll());
    //  Needed to hydrate the Teams column
    dispatch(TeamsModule.actions.fetchAll());
  });

  useEffect(() => {
    if (organization) {
      dispatch(UsersModule.actions.extractFromOrganization(organization));
      dispatch(OrganizationAccessRequestsModule.actions.extractFromOrganization(organization));
    }
  }, [dispatch, organization]);

  if (!organization || usersStatus.isLoading || organizationStatus.isLoading) {
    return (
      <div className="users-index padding-top-lg">
        <Loader size={80} />
      </div>
    );
  }

  return (
    <ConnectedOrganizationUsersIndex
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      organization={organization}
    />
  );
};

export default OrganizationUsersIndexContainer;
